import React, { useEffect, useState } from 'react';
import { X, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { GALLERY } from '../data/gallery';
import MsRubiesBadge from '../components/MsRubiesBadge';

export default function GalleryPage() {
    const [open, setOpen] = useState(null);

    return (
        <div data-testid="gallery-page">
            <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-10">
                <div className="flex items-end justify-between flex-wrap gap-6">
                    <div className="max-w-2xl">
                        <div className="text-xs uppercase tracking-[0.3em] text-brand-aqua mb-3 flex items-center gap-3">
                            <i className="fa-solid fa-camera-retro text-brand-orange"></i> Real trips · Real clients
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter">
                            The <span className="text-gradient-sunset">moment</span>s we made
                        </h1>
                        <p className="text-white/70 mt-5">From boarding day at Port Canaveral to the rooftop lounge at sunset — these are real photos from real Cruise Vibes clients. Could be you next.</p>
                    </div>
                    <MsRubiesBadge variant="stamp-light" />
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {GALLERY.map((g, i) => (
                        <button
                            key={i}
                            onClick={() => setOpen(i)}
                            data-testid={`gallery-item-${i}`}
                            className="group relative aspect-[3/4] overflow-hidden rounded-2xl border border-white/5 hover:border-brand-gold/60 transition-colors"
                        >
                            {g.type === 'video' ? (
                                <>
                                    <video
                                        src={g.src}
                                        muted
                                        loop
                                        playsInline
                                        autoPlay
                                        preload="metadata"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.1]"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <span className="h-12 w-12 rounded-full bg-brand-gold/90 flex items-center justify-center shadow-lg">
                                            <Play size={20} className="text-brand-bg ml-0.5" fill="currentColor" />
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <img
                                    src={g.src}
                                    alt={g.caption}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.4]"
                                    style={{ transform: 'scale(1.3)', objectPosition: 'center 55%' }}
                                />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-aqua">{g.tag} · {g.date}</div>
                                <div className="font-display text-sm sm:text-base uppercase tracking-tight mt-1 line-clamp-2">{g.caption}</div>
                                <div className="text-xs text-white/65 mt-1 line-clamp-1">{g.location}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {open !== null && (
                <Lightbox
                    item={GALLERY[open]}
                    index={open}
                    total={GALLERY.length}
                    onClose={() => setOpen(null)}
                    onPrev={() => setOpen((i) => (i - 1 + GALLERY.length) % GALLERY.length)}
                    onNext={() => setOpen((i) => (i + 1) % GALLERY.length)}
                />
            )}
        </div>
    );
}

function Lightbox({ item, index, total, onClose, onPrev, onNext }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') onPrev();
            else if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose, onPrev, onNext]);

    return (
        <div
            data-testid="gallery-lightbox"
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-brand-bg/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        >
            <button
                data-testid="lightbox-close"
                onClick={onClose}
                className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white z-10"
                aria-label="Close"
            >
                <X size={20} />
            </button>
            <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-bg flex items-center justify-center text-white z-10 transition-colors"
                aria-label="Previous"
            >
                <ChevronLeft size={22} />
            </button>
            <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-bg flex items-center justify-center text-white z-10 transition-colors"
                aria-label="Next"
            >
                <ChevronRight size={22} />
            </button>

            <div
                className="w-full max-w-6xl flex flex-col items-center gap-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="w-full flex items-center justify-center bg-black/40 rounded-2xl border border-white/10 overflow-hidden" style={{ maxHeight: '78vh' }}>
                    {item.type === 'video' ? (
                        <video
                            key={item.src}
                            src={item.src}
                            controls
                            autoPlay
                            playsInline
                            className="block max-w-full bg-black"
                            style={{ maxHeight: '78vh' }}
                        />
                    ) : (
                        <img
                            key={item.src}
                            src={item.src}
                            alt={item.caption}
                            className="block max-w-full"
                            style={{ maxHeight: '78vh', objectFit: 'contain' }}
                        />
                    )}
                </div>
                <div className="text-center max-w-3xl">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand-aqua">{item.tag} · {item.date}</div>
                    <div className="font-display text-2xl sm:text-3xl uppercase tracking-tight mt-2">{item.caption}</div>
                    <div className="text-sm text-white/65 mt-1.5">{item.location}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/35 mt-3">{index + 1} of {total}</div>
                </div>
            </div>
        </div>
    );
}
